import React, { useState } from 'react';
import { Box, Typography, IconButton, Container, Button, Avatar, Menu, MenuItem, ListItemText, CircularProgress, Snackbar, Alert } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ambientBackground } from './portalStyles';

const NAV_LINKS = [
    { label: 'Quotes',    path: '/portal/quotes' },
    { label: 'Contracts', path: '/portal/contracts' },
    { label: 'Invoices',  path: '/portal/invoices' },
    { label: 'Projects',  path: '/portal/projects' },
];

function initials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : name.slice(0, 2)).toUpperCase();
}

export default function PortalLayout({ children }) {
    const { customerName, clientName, clientLogoBase64, memberships, switchCompany, logout } = usePortalAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [switcherAnchor, setSwitcherAnchor] = useState(null);
    const [switching, setSwitching] = useState(false);
    const [switchError, setSwitchError] = useState(false);
    const canSwitch = (memberships || []).length > 1;

    const handleSwitch = async (membership) => {
        setSwitcherAnchor(null);
        if (membership.clientName === clientName && membership.customerName === customerName) return;
        setSwitching(true);
        try {
            await switchCompany(membership.customerId);
            // Full reload so every page refetches under the new company scope.
            window.location.href = '/portal/quotes';
        } catch {
            setSwitching(false);
            setSwitchError(true);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', background: ambientBackground, backgroundAttachment: 'fixed', display: 'flex', flexDirection: 'column' }}>
            {/* ── Header — light, tenant-branded, app-consistent ── */}
            <Box component="header" sx={{ backgroundColor: '#fff', borderBottom: '1px solid #e6ecf1' }}>
                <Box sx={{ height: 3, background: 'linear-gradient(90deg, #427898, #1e3a52)' }} />

                <Container maxWidth="lg">
                    {/* Top row: tenant identity + customer + logout */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                        <Box
                            onClick={canSwitch && !switching ? (e) => setSwitcherAnchor(e.currentTarget) : undefined}
                            title={canSwitch ? 'Switch company' : undefined}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
                                py: 0.5, pl: 0.5, pr: canSwitch ? 1.25 : 0.5, borderRadius: '999px',
                                cursor: canSwitch && !switching ? 'pointer' : 'default',
                                transition: 'background-color .15s',
                                ...(canSwitch && { '&:hover': { backgroundColor: '#eef4f8' } }),
                            }}
                        >
                            {clientLogoBase64 ? (
                                <Box
                                    component="img"
                                    src={`data:image/png;base64,${clientLogoBase64}`}
                                    alt={clientName || 'Client logo'}
                                    sx={{ height: 32, width: 'auto', maxWidth: 150, objectFit: 'contain', flexShrink: 0 }}
                                />
                            ) : (
                                <Box sx={{ width: 32, height: 32, borderRadius: '8px', flexShrink: 0, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, #427898, #1e3a52)', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                                    {initials(clientName)}
                                </Box>
                            )}
                            <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: '#17262f' }}>
                                {clientName || 'Customer Portal'}
                            </Typography>
                            {canSwitch && (
                                switching
                                    ? <CircularProgress size={15} sx={{ color: '#427898', ml: 0.25 }} />
                                    : <ExpandMoreIcon sx={{ fontSize: 19, color: '#7f8c9a', ml: -0.25 }} />
                            )}
                        </Box>
                        {canSwitch && (
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5,
                                    fontSize: 11, color: '#94a3b8',
                                }}
                            >
                                <ApartmentIcon sx={{ fontSize: 13 }} />
                                {memberships.length} companies — click to switch
                            </Box>
                        )}
                        <Menu
                            anchorEl={switcherAnchor}
                            open={Boolean(switcherAnchor)}
                            onClose={() => setSwitcherAnchor(null)}
                        >
                            {(memberships || []).map((m) => {
                                const isCurrent = m.clientName === clientName && m.customerName === customerName;
                                return (
                                    <MenuItem key={m.customerId} selected={isCurrent} onClick={() => handleSwitch(m)}>
                                        {m.clientLogoBase64 ? (
                                            <Box
                                                component="img"
                                                src={`data:image/png;base64,${m.clientLogoBase64}`}
                                                alt={m.clientName}
                                                sx={{ width: 24, height: 24, objectFit: 'contain', mr: 1.25 }}
                                            />
                                        ) : (
                                            <Box sx={{ width: 24, height: 24, borderRadius: '6px', mr: 1.25, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, #427898, #1e3a52)', color: '#fff', fontSize: 10, fontWeight: 700 }}>
                                                {initials(m.clientName)}
                                            </Box>
                                        )}
                                        <ListItemText
                                            primary={m.clientName}
                                            secondary={m.customerName}
                                            primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }}
                                            secondaryTypographyProps={{ fontSize: 11.5 }}
                                        />
                                    </MenuItem>
                                );
                            })}
                        </Menu>

                        <Box sx={{ flex: 1 }} />

                        {customerName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: '#eef4f8', color: '#1e3a52' }}>
                                    {initials(customerName)}
                                </Avatar>
                                <Typography sx={{ fontSize: 12.5, color: '#40505d', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                                    {customerName}
                                </Typography>
                            </Box>
                        )}
                        <IconButton
                            size="small"
                            onClick={logout}
                            sx={{ color: '#94a3b8', '&:hover': { color: '#1e3a52', backgroundColor: '#eef4f8' } }}
                            title="Sign out"
                        >
                            <LogoutIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Nav row: pill tabs */}
                    <Box sx={{ display: 'flex', gap: 0.5, pb: 1, overflowX: 'auto' }}>
                        {NAV_LINKS.map((link) => {
                            const active = location.pathname.startsWith(link.path);
                            return (
                                <Button
                                    key={link.path}
                                    onClick={() => navigate(link.path)}
                                    size="small"
                                    disableRipple
                                    sx={{
                                        color: active ? '#1e3a52' : '#64748b',
                                        fontWeight: active ? 600 : 500,
                                        fontSize: 13,
                                        backgroundColor: active ? '#eef4f8' : 'transparent',
                                        borderRadius: '9px',
                                        px: 1.5,
                                        py: 0.75,
                                        textTransform: 'none',
                                        flexShrink: 0,
                                        '&:hover': { color: '#1e3a52', backgroundColor: '#eef4f8' },
                                    }}
                                >
                                    {link.label}
                                </Button>
                            );
                        })}
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
                {children}
            </Container>

            <Snackbar
                open={switchError}
                autoHideDuration={4000}
                onClose={() => setSwitchError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setSwitchError(false)} sx={{ width: '100%' }}>
                    Could not switch company. Please try again.
                </Alert>
            </Snackbar>
        </Box>
    );
}
